import React from 'react';

/**
 * Panneau d'ajustements (colonne droite)
 */
export function RightPanelAdjustments({ 
  adjustments, 
  onAdjustmentChange, 
  onRecalculate,
  employee,
  monthlyHours,
  autoRecalc = true
}) {
  const handleChange = (field, value) => {
    onAdjustmentChange(field, value);
  };
  const money = v => v === '' || v == null ? '' : String(v);
  
  // Calculer les heures contractuelles mensuelles (151.67h pour 35h/semaine)
  const weeklyHours = parseFloat(employee?.weekly_hours || 35);
  const contractualMonthlyHours = (weeklyHours * 52) / 12; // ~151.67h pour 35h/semaine
  
  // Heures réelles (depuis le tableau ou saisie manuelle)
  const realHours = adjustments.hours_overwrite || monthlyHours || contractualMonthlyHours;
  
  // Calculer l'excédent automatique
  const overtimeExcess = Math.max(0, realHours - contractualMonthlyHours);

  return (
    <div className="payroll-right-panel">
      <div className="payroll-panel-sticky">
        <h3 className="payroll-panel-title">Ajustements</h3>

        {/* Heures */}
        <div className="payroll-panel-section">
          <h4 className="payroll-panel-subtitle">Base horaire</h4>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">
              Heures du mois
              <span className="payroll-panel-hint">(modifiable)</span>
            </label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={adjustments.hours_overwrite || monthlyHours || ''}
              onChange={(e) => handleChange('hours_overwrite', e.target.value)}
              className="payroll-panel-input"
            />
          </div>
          {overtimeExcess > 0 && (
            <div className="payroll-info-bubble">
              ℹ️ Dépassement de {overtimeExcess.toFixed(2)}h par rapport au contrat ({contractualMonthlyHours.toFixed(2)}h). Ces heures sont considérées comme supplémentaires.
            </div>
          )}
        </div>

        {/* Congés & Absences */}
        <div className="payroll-panel-section">
          <h4 className="payroll-panel-subtitle">Congés & Absences</h4>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Congés payés (jours)</label>
            <input
              type="number"
              min="0"
              max="31"
              value={adjustments.paid_leave_days || 0}
              onChange={(e) => handleChange('paid_leave_days', e.target.value)}
              className="payroll-panel-input"
              placeholder="0"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Congés sans solde (jours)</label>
            <input
              type="number"
              min="0"
              max="31"
              value={adjustments.unpaid_leave_days || 0}
              onChange={(e) => handleChange('unpaid_leave_days', e.target.value)}
              className="payroll-panel-input"
              placeholder="0"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Arrêt maladie (jours)</label>
            <input
              type="number"
              min="0"
              max="31"
              value={adjustments.sick_days || 0}
              onChange={(e) => handleChange('sick_days', e.target.value)}
              className="payroll-panel-input"
              placeholder="0"
            />
          </div>
        </div>

        {/* Heures supplémentaires */}
        <div className="payroll-panel-section">
          <h4 className="payroll-panel-subtitle">Heures supplémentaires</h4>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Heures supplémentaires 1</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={adjustments.overtime_hours_125 || 0}
              onChange={(e) => handleChange('overtime_hours_125', e.target.value)}
              className="payroll-panel-input"
              placeholder="0"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Heures supplémentaires 2</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={adjustments.overtime_hours_150 || 0}
              onChange={(e) => handleChange('overtime_hours_150', e.target.value)}
              className="payroll-panel-input"
              placeholder="0"
            />
          </div>
        </div>

        {/* Primes */}
        <div className="payroll-panel-section">
          <h4 className="payroll-panel-subtitle">Primes</h4>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Prime fixe (€)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.bonus_fixed)}
              onChange={(e) => handleChange('bonus_fixed', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Prime exceptionnelle (€)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.bonus_exceptional)}
              onChange={(e) => handleChange('bonus_exceptional', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Prime d'ancienneté (€)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.bonus_seniority)}
              onChange={(e) => handleChange('bonus_seniority', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Autre prime (€)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.bonus_other)}
              onChange={(e) => handleChange('bonus_other', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Avantages en nature */}
        <div className="payroll-panel-section">
          <h4 className="payroll-panel-subtitle">Avantages en nature</h4>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Véhicule (€/mois)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.benefit_vehicle)}
              onChange={(e) => handleChange('benefit_vehicle', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Logement (€/mois)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.benefit_housing)}
              onChange={(e) => handleChange('benefit_housing', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
          <div className="payroll-panel-field">
            <label className="payroll-panel-label">Repas (€/mois)</label>
            <input
              type="number"
              step="0.01"
              value={money(adjustments.benefit_meals)}
              onChange={(e) => handleChange('benefit_meals', e.target.value)}
              className="payroll-panel-input"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Titres-restaurant override */}
        {employee?.meal_vouchers_enabled && (
          <div className="payroll-panel-section">
            <h4 className="payroll-panel-subtitle">Titres-restaurant</h4>
            <div className="payroll-panel-field">
              <label className="payroll-panel-label">
                Jours éligibles ce mois
                <span className="payroll-panel-hint">(override)</span>
              </label>
              <input
                type="number"
                min="0"
                max="31"
                value={adjustments.days_eligible_override || ''}
                onChange={(e) => handleChange('days_eligible_override', e.target.value)}
                className="payroll-panel-input"
                placeholder={`Auto (${employee.meal_voucher_days_per_month || 18})`}
              />
            </div>
          </div>
        )}

        {/* Info */}
        <div className="payroll-panel-info">
          <div className="payroll-panel-info-icon">ℹ️</div>
          <div className="payroll-panel-info-text">
            Modifiez les champs puis cliquez sur "Recalculer" pour mettre à jour la fiche.
            Les barèmes utilisés sont approximatifs et doivent être validés.
          </div>
        </div>
      </div>
    </div>
  );
}
